import React from 'react';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { X, Check } from 'lucide-react';

const SelectionModal = ({
  product, selectedOptions, selectedSupplements,
  getOptionGroups, getGroupLabel, getRequiredCount,
  onSelectOption, onToggleSupplement,
  isComplete, onConfirm, onClose, getProductName
}) => {
  if (!product) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="selection-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg text-gray-900">{getProductName(product)}</h3>
            <p className="text-green-600 font-semibold">{(Number(product.price) || 0).toFixed(2)}€</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
            <X size={20} />
          </button>
        </div>
        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-5">
            {Object.entries(getOptionGroups(product)).map(([group, options]) => {
              const required = getRequiredCount(product, group);
              const selected = selectedOptions[group] || [];
              const isGroupComplete = required > 0 && selected.length >= required;
              return (
                <div key={group}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800">{getGroupLabel(group)}</h4>
                    {required > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isGroupComplete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {isGroupComplete && <Check size={12} className="inline mr-0.5" />}
                        {selected.length}/{required}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {options.map((option, idx) => {
                      const optName = option.name?.es || option.name || '';
                      const isSelected = selected.some(o => (o.name?.es || o.name) === optName);
                      return (
                        <button key={idx} onClick={() => onSelectOption(group, option)}
                          className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-between ${isSelected ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                          <span className="font-medium">{optName}</span>
                          <div className="flex items-center gap-2">
                            {Number(option.price_modifier) > 0 && <span className="text-sm text-gray-500">+{Number(option.price_modifier).toFixed(2)}€</span>}
                            {isSelected && <Check size={18} className="text-green-600" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {product.supplements?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Extras (opcional)</h4>
                <div className="space-y-1.5">
                  {product.supplements.map((sup, idx) => {
                    const supName = sup.name?.es || sup.name || '';
                    const isSelected = selectedSupplements.some(s => (s.name?.es || s.name) === supName);
                    return (
                      <button key={idx} onClick={() => onToggleSupplement(sup)}
                        className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-between ${isSelected ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                        <span className="font-medium">{supName}</span>
                        <div className="flex items-center gap-2">
                          {Number(sup.price_modifier) > 0 && <span className="text-sm text-gray-500">+{Number(sup.price_modifier).toFixed(2)}€</span>}
                          {isSelected && <Check size={18} className="text-blue-600" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
        <div className="px-5 py-4 border-t bg-gray-50 rounded-b-2xl">
          <Button onClick={onConfirm} disabled={!isComplete} className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg disabled:opacity-50" data-testid="confirm-selection-btn">
            {isComplete ? <><Check size={20} className="mr-2" />Añadir al pedido</> : 'Completa las selecciones'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectionModal;
